// Fixed-size uniform grid that recentres on the player each frame. Allocation-free after construction.
export class SpatialGrid {
  private readonly head: Int32Array;
  private readonly next: Int32Array;
  private originX = 0;
  private originZ = 0;

  constructor(
    private readonly cell: number,
    private readonly dim: number,
    capacity: number,
  ) {
    this.head = new Int32Array(dim * dim).fill(-1);
    this.next = new Int32Array(capacity).fill(-1);
  }

  rebuild(count: number, xs: (i: number) => number, zs: (i: number) => number, ids: ArrayLike<number>, cx: number, cz: number): void {
    this.head.fill(-1);
    const half = (this.dim * this.cell) / 2;
    this.originX = cx - half;
    this.originZ = cz - half;
    for (let n = 0; n < count; n += 1) {
      const id = ids[n];
      const ix = Math.floor((xs(id) - this.originX) / this.cell);
      const iz = Math.floor((zs(id) - this.originZ) / this.cell);
      if (ix < 0 || iz < 0 || ix >= this.dim || iz >= this.dim) continue;
      const c = iz * this.dim + ix;
      this.next[id] = this.head[c];
      this.head[c] = id;
    }
  }

  // Writes candidate ids whose cell overlaps the circle into `out`; returns how many.
  query(x: number, z: number, r: number, out: Int32Array): number {
    const x0 = Math.max(0, Math.floor((x - r - this.originX) / this.cell));
    const x1 = Math.min(this.dim - 1, Math.floor((x + r - this.originX) / this.cell));
    const z0 = Math.max(0, Math.floor((z - r - this.originZ) / this.cell));
    const z1 = Math.min(this.dim - 1, Math.floor((z + r - this.originZ) / this.cell));
    let n = 0;
    for (let iz = z0; iz <= z1; iz += 1) {
      for (let ix = x0; ix <= x1; ix += 1) {
        for (let id = this.head[iz * this.dim + ix]; id !== -1; id = this.next[id]) {
          if (n >= out.length) return n;
          out[n++] = id;
        }
      }
    }
    return n;
  }
}
